import { useCallback, useEffect, useRef, useState } from 'react';
import { buildPlaceUrls, contract, PlaceTypeKeyEnum, PlaceTypeNameEnum, TransactionTypeEnum, } from '@aviv_price-pages/brand';
import { useLocale } from '@aviv-unified-frontend/runtime/i18n';
import { LngLat } from 'mapbox-gl';
import HttpClient from '@/adapters/http/HttpClient';
import { MediumCta } from '@/entities/enums/MediumCta';
import { appendEphemeralParam } from '@/helpers/ephemeral/ephemeral';
import { getPlaceBestDescendantUrl, getPlacesWithGeometry } from '@/helpers/geo/places';
import { convertToMapBoxLngLatBounds } from '@/helpers/map/convertToMapBoxLngLatBounds';
import { removeElementFocusability, removeMapControlsFocusability } from '@/helpers/map/dom';
import { addAllPriceLayers, addPlaceOutlineLayer, addTileLayer, getColorScaleByLevel, getLayerSourceName, removeLayers, selectionBackgroundLayerBuilder, selectionIndicatorLayerBuilder, selectionOutlineLayerBuilder, } from '@/helpers/map/layers';
import { adjustZoomRanges, getZoomRangesByTransactionType } from '@/helpers/map/zoomRanges';
import { priceTileJsonMapper } from '@/helpers/priceTileJson/priceTileJsonMapper';
import { normalizePath } from '@/helpers/redirect/redirect';
import { tileJsonMapper } from '@/helpers/tileJson/tileJsonMapper';
import { getUrlWithMediumAttribute } from '@/helpers/tracking/tracking';
import { getPlaceForPricesLayers } from '@/hooks/useMap/helper';
import { useMapbox } from '@/hooks/useMapbox/useMapbox';
import { usePrevious } from '@/hooks/usePrevious/usePrevious';
export var MapEvents;
(function (MapEvents) {
    MapEvents["PLACE_LAYERS_ADDED"] = "placeLayersAdded";
    MapEvents["PRICE_LAYERS_ADDED"] = "priceLayersAdded";
    MapEvents["LOAD"] = "load";
    MapEvents["IDLE"] = "idle";
    MapEvents["SHOW_BUILDING_HOUSE_NUMBERS"] = "showBuildingHouseNumbers";
    MapEvents["NAVIGATE_TO_URL"] = "navigateToUrl";
    MapEvents["REMOVE_POPUP"] = "removePopup";
    MapEvents["SHOW_AGENCY_CARD"] = "showAgencyCard";
})(MapEvents || (MapEvents = {}));
let debounce = 0;
const withAgencyPins = contract?.microFrontends?.mfe_avis_pricePagesAgencyCards;
const agenciesLayerId = 'agencies-layer';
const otherFeaturesLayers = withAgencyPins
    ? [agenciesLayerId, `${agenciesLayerId}-hover`, `${agenciesLayerId}-active`]
    : [];
export const MARKER_SIZE = 2;
export const FEATURES_MIN_ZOOM = 9;
export const ZOOM_BUFFER = 0.1;
export const MIN_ZOOM_SMOOTHING_MS = 1500;
const CURRENT_PLACE_OUTLINE_LAYER_ID = 'CURRENT_PLACE_OUTLINE';
const SELECTION_LAYERS_ID = 'SELECTION_LAYERS';
const PRICE_LAYERS_ID = 'PRICE_LAYERS';
export function getZoomBounds({ place, layers }) {
    if (layers.length === 0)
        return {};
    const zoomOutlinePlaceType = contract.geoLevelZoomOutline?.[place.typeKey];
    const targetLayer = zoomOutlinePlaceType
        ? layers.find((l) => l.place?.type === zoomOutlinePlaceType)
        : layers[0];
    return {
        minZoom: targetLayer?.minzoom ? targetLayer.minzoom + ZOOM_BUFFER : undefined,
        maxZoom: targetLayer?.maxzoom ? targetLayer.maxzoom - ZOOM_BUFFER : undefined,
    };
}
export function ensureZoomWithinLayerBounds({ map, place, layers, hasForcedZoomRef, }) {
    if (hasForcedZoomRef.current || layers.length === 0)
        return;
    const currentZoom = map.getZoom();
    const { minZoom, maxZoom } = getZoomBounds({ place, layers });
    const zoomSmoothingRateMs = 1000;
    const enforceZoom = (targetZoom) => {
        const delta = Math.abs(currentZoom - targetZoom);
        map.zoomTo(targetZoom, {
            duration: Math.max(delta * zoomSmoothingRateMs, MIN_ZOOM_SMOOTHING_MS),
        });
        hasForcedZoomRef.current = true;
    };
    if (minZoom !== undefined && currentZoom < minZoom) {
        enforceZoom(minZoom);
    }
    else if (maxZoom !== undefined && currentZoom > maxZoom) {
        enforceZoom(maxZoom);
    }
}
async function safeNavigateOnMap(map, url) {
    const redirects = contract.redirects || [];
    // Parse the pathname only from the full URL
    const parsedUrl = new URL(url, window.location.origin);
    const rawPathname = parsedUrl.pathname;
    const normalizedPath = normalizePath(rawPathname);
    const fullCanonicalUrl = normalizedPath + parsedUrl.search;
    // If user is trying to navigate to a raw redirect source AND we didn't normalize it, block it
    const isRedirectSource = redirects.some((r) => rawPathname === `${contract.basePath}${r.source}`);
    if (isRedirectSource && normalizedPath === rawPathname) {
        return;
    }
    if (fullCanonicalUrl !== url) {
        map.fire(MapEvents.NAVIGATE_TO_URL, { url: fullCanonicalUrl });
    }
    else {
        map.fire(MapEvents.NAVIGATE_TO_URL, { url });
    }
}
export function useMap(place) {
    const previousPlace = usePrevious(place);
    const mapContainerRef = useRef(null);
    const currentPlaceOutlineLayers = useRef([]);
    const currentPlacePriceLayers = useRef([]);
    const selectionLayers = useRef([]);
    const currentLayers = useRef([]);
    const activePriceDate = useRef('');
    const placeTileApiJSON = useRef(undefined);
    const currentAgencyId = useRef('');
    const hasForcedZoom = useRef(false);
    const [mapLegendData, setMapLegendData] = useState();
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [priceLayersAdded, setPriceLayersAdded] = useState(false);
    const [placeLayersAdded, setPlaceLayersAdded] = useState(false);
    const [agencyPoints, setAgencyPoints] = useState(null);
    const httpClient = HttpClient.getInstance();
    const { language } = useLocale();
    const map = useMapbox({
        container: mapContainerRef,
        locale: language,
        defaultBounds: place.viewport ? convertToMapBoxLngLatBounds(place.viewport) : undefined,
    });
    const debounceResize = useRef(0);
    const onResize = useCallback(() => {
        if (debounceResize.current) {
            window.clearTimeout(debounceResize.current);
        }
        debounceResize.current = window.setTimeout(() => {
            map?.resize();
            map?.triggerRepaint();
            debounceResize.current = 0;
        }, 200);
    }, [map]);
    useEffect(() => {
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            if (debounceResize.current) {
                window.clearTimeout(debounceResize.current);
                debounceResize.current = 0;
            }
        };
    }, [onResize]);
    const retrieveTileApiJSON = useCallback(async () => {
        return httpClient.get(`${process.env.TILE_API_URL}relation/describe`, undefined, process.env.GEO_SERVICE_APIKEY);
    }, [httpClient]);
    const getTileApiJSON = useCallback(async () => {
        if (placeTileApiJSON.current === undefined) {
            placeTileApiJSON.current = await retrieveTileApiJSON();
            return placeTileApiJSON.current;
        }
        return placeTileApiJSON.current;
    }, [retrieveTileApiJSON]);
    const getTileLayers = useCallback(async () => {
        const tileApiLayerJSON = await getTileApiJSON();
        return tileApiLayerJSON.layers.map(tileJsonMapper);
    }, [getTileApiJSON]);
    const getTileLayerByPlaceType = useCallback(async (placeType) => {
        const tileApiLayerJSON = await getTileApiJSON();
        const tileApiLayer = tileApiLayerJSON.layers.find((tile) => tile.type === placeType);
        if (!tileApiLayer) {
            return;
        }
        return tileJsonMapper(tileApiLayer);
    }, [getTileApiJSON]);
    const getActivePriceDate = useCallback(async (country) => {
        const date = await httpClient.get(`${process.env.PRICE_TILE_API_URL}v1/layer/active_price_date/${country}`, undefined, process.env.PRICE_TILE_API_KEY);
        return date.active_price_date;
    }, [httpClient]);
    const getPriceTilesById = useCallback(async (placeId) => {
        if (!activePriceDate.current) {
            activePriceDate.current = await getActivePriceDate(contract.country);
        }
        const pricesLayerTileJson = await httpClient.get(`${process.env.PRICE_TILE_API_URL}v1/layer/${TransactionTypeEnum.SELL}/${placeId}/${activePriceDate.current}?fallback=true`, undefined, process.env.PRICE_TILE_API_KEY);
        return priceTileJsonMapper(pricesLayerTileJson);
    }, [getActivePriceDate, httpClient]);
    /**
     * Add an outline layer to indicate the current place
     */
    const addCurrentPlaceOutline = useCallback(async (map, place, placeForPricesLayers) => {
        const placeType = place.typeKey;
        const placeId = place.id;
        if (placeType === PlaceTypeKeyEnum.HOUSE_NUMBER) {
            const parcel = place.parents?.[PlaceTypeNameEnum.PARCEL];
            if (parcel) {
                const tileJSON = await getTileLayerByPlaceType(PlaceTypeKeyEnum.PARCEL);
                if (tileJSON) {
                    addPlaceOutlineLayer({
                        map,
                        layer: tileJSON,
                        placeId: parcel.id,
                        layersArray: currentPlaceOutlineLayers.current,
                        beforeLayer: CURRENT_PLACE_OUTLINE_LAYER_ID,
                    });
                }
            }
            else if (place.buildings) {
                const { buildings } = place;
                const tileJSON = await getTileLayerByPlaceType(PlaceTypeKeyEnum.BUILDING);
                if (tileJSON) {
                    addPlaceOutlineLayer({
                        map,
                        layer: tileJSON,
                        placeId: buildings[0].id,
                        layersArray: currentPlaceOutlineLayers.current,
                        beforeLayer: CURRENT_PLACE_OUTLINE_LAYER_ID,
                    });
                }
            }
        }
        else if (placeType === PlaceTypeKeyEnum.BOROUGH) {
            const tileJSON = await getTileLayerByPlaceType(placeForPricesLayers.typeKey);
            if (tileJSON) {
                addPlaceOutlineLayer({
                    map,
                    layer: tileJSON,
                    placeId: placeForPricesLayers.id,
                    layersArray: currentPlaceOutlineLayers.current,
                    beforeLayer: CURRENT_PLACE_OUTLINE_LAYER_ID,
                });
            }
        }
        else if (place.buildings) {
            const { buildings } = place;
            const tileJSON = await getTileLayerByPlaceType(PlaceTypeKeyEnum.BUILDING);
            if (tileJSON) {
                addPlaceOutlineLayer({
                    map,
                    layer: tileJSON,
                    placeId: buildings[0].id,
                    layersArray: currentPlaceOutlineLayers.current,
                    beforeLayer: CURRENT_PLACE_OUTLINE_LAYER_ID,
                });
            }
        }
        else if (getPlacesWithGeometry(contract).includes(placeType)) {
            const tileJSON = await getTileLayerByPlaceType(placeType);
            if (tileJSON) {
                addPlaceOutlineLayer({
                    map,
                    layer: tileJSON,
                    placeId,
                    layersArray: currentPlaceOutlineLayers.current,
                    beforeLayer: CURRENT_PLACE_OUTLINE_LAYER_ID,
                });
            }
        }
    }, [getTileLayerByPlaceType]);
    const displayBuildingHouseNumbers = useCallback((lngLat, houseNumbers) => {
        map?.fire(MapEvents.SHOW_BUILDING_HOUSE_NUMBERS, { lngLat, houseNumbers });
    }, [map]);
    const updateOutlineLayerSelection = useCallback((map, placeId) => {
        if (!placeId) {
            return;
        }
        const outlineLayerId = currentPlaceOutlineLayers.current[0];
        map.setFilter(outlineLayerId, ['in', 'feature_id', placeId]);
    }, []);
    const handleBuildingClick = useCallback(async (map, placeId, lngLat, originPlaceType) => {
        const parentsToBuildUrl = contract?.urls?.parentsTypeByPlaceType?.[PlaceTypeKeyEnum.HOUSE_NUMBER];
        const parentTypes = parentsToBuildUrl
            ? parentsToBuildUrl.map((type) => `${type}`).join(',')
            : PlaceTypeKeyEnum.STREET;
        const addresses = await httpClient.get(`${process.env.BFF_BASE_URL}/relation?id=${placeId}&language=${language}&placeType=${PlaceTypeKeyEnum.HOUSE_NUMBER}&parentTypes=${parentTypes}`, undefined, process.env.BFF_API_KEY);
        if (addresses.length === 0)
            return;
        if (addresses.length === 1) {
            if (addresses[0].viewport) {
                map.fitBounds(convertToMapBoxLngLatBounds(addresses[0].viewport));
            }
            const baseUrl = (await buildPlaceUrls(contract.nameAbbreviation, [addresses[0]]))[0];
            const url = appendEphemeralParam(getUrlWithMediumAttribute({
                url: baseUrl,
                cta: MediumCta.MAP,
                originPlaceType: originPlaceType,
                destination: addresses[0].type,
            }));
            safeNavigateOnMap(map, url);
        }
        if (addresses.length > 1) {
            updateOutlineLayerSelection(map, placeId);
            displayBuildingHouseNumbers(lngLat, addresses);
        }
    }, [displayBuildingHouseNumbers, httpClient, language, updateOutlineLayerSelection]);
    /**
     * Add interaction for selecting place layer
     * @param map
     * @param layerData
     * @param selectionType Describes if the user select a place whithin the current or around
     * @param filterExpression Permit to filter selectable places
     */
    const addSelectionLayers = useCallback((map, layerData, selectionType, filterExpression) => {
        // Here we can not use the map box setFeatureState to change the color of an hovered area
        // because we can not change features' order, it results some display issue.
        // So we need to create 3 layers
        const layerBaseId = `selection-${selectionType}-${layerData.name}-${place.id}`;
        const fillLayerId = `${layerBaseId}-fill`;
        const outlineLayerId = `${layerBaseId}-outline`;
        const indicatorLayerId = `${layerBaseId}-indicator`;
        // Add an invisible fill layer for the hover selection area
        addTileLayer({
            layerId: fillLayerId,
            tiles: layerData.tiles,
            sourceLayer: selectionType === 'around' ? layerData.type : layerData.name,
            minzoom: layerData.minzoom,
            maxzoom: layerData.maxzoom,
            layerStyleBuilder: selectionBackgroundLayerBuilder(layerData.minzoom, layerData.maxzoom),
            map,
            layersArray: selectionLayers.current,
            beforeLayer: SELECTION_LAYERS_ID,
        });
        // Add an outline layer to indicate the different selection areas
        addTileLayer({
            layerId: indicatorLayerId,
            tiles: layerData.tiles,
            sourceLayer: selectionType === 'around' ? layerData.type : layerData.name,
            minzoom: layerData.minzoom,
            maxzoom: layerData.maxzoom,
            layerStyleBuilder: selectionIndicatorLayerBuilder(layerData.minzoom, layerData.maxzoom),
            map,
            layersArray: selectionLayers.current,
            beforeLayer: SELECTION_LAYERS_ID,
        });
        // Add an outline layer who will be displayed when the user hover the selection area
        addTileLayer({
            layerId: outlineLayerId,
            tiles: layerData.tiles,
            sourceLayer: selectionType === 'around' ? layerData.type : layerData.name,
            minzoom: layerData.minzoom,
            maxzoom: layerData.maxzoom,
            layerStyleBuilder: selectionOutlineLayerBuilder(layerData.minzoom, layerData.maxzoom),
            map,
            layersArray: selectionLayers.current,
            beforeLayer: SELECTION_LAYERS_ID,
        });
        // Initial filter on the outline selection layer to not display any place
        map.setFilter(outlineLayerId, ['in', 'feature_id', '']);
        // Filter to not display the current place and to exclude the place from other country
        const allFilters = [
            'all',
            ['in', contract.country, ['get', 'feature_id']],
            ...(filterExpression ? [filterExpression] : []),
        ];
        map.setFilter(fillLayerId, allFilters);
        map.setFilter(indicatorLayerId, allFilters);
        const mapClickHandler = async ({ features, lngLat, point, }, originPlaceType) => {
            // There is an issue with the layer priority
            // This is to avoid to click on this layer when user click on an agency pin
            // This is not ideal...
            const othersFeatures = map.queryRenderedFeatures(point, {
                layers: otherFeaturesLayers,
            });
            if (!features || features.length === 0 || othersFeatures.length > 0)
                return;
            const feature = features[0];
            const placeId = feature.properties.feature_id;
            const place = await httpClient.get(`${process.env.BFF_BASE_URL}/place?id=${placeId}&locale=${language}`, undefined, process.env.BFF_API_KEY);
            if (place.typeKey === PlaceTypeKeyEnum.BUILDING) {
                await handleBuildingClick(map, placeId, lngLat, originPlaceType);
            }
            else if (contract.geoConfig[place.typeKey].active) {
                if (place.active) {
                    const baseUrl = (await buildPlaceUrls(contract.nameAbbreviation, [place]))[0];
                    const url = appendEphemeralParam(getUrlWithMediumAttribute({
                        url: baseUrl,
                        cta: MediumCta.MAP,
                        originPlaceType: originPlaceType,
                        destination: place.type,
                    }));
                    safeNavigateOnMap(map, url);
                }
                else {
                    const baseUrl = await getPlaceBestDescendantUrl(place);
                    if (baseUrl) {
                        const url = appendEphemeralParam(getUrlWithMediumAttribute({
                            url: baseUrl,
                            cta: MediumCta.MAP,
                            originPlaceType: originPlaceType,
                            destination: place.type,
                        }));
                        safeNavigateOnMap(map, url);
                    }
                }
            }
            else {
                if (place.viewport) {
                    map.fitBounds(convertToMapBoxLngLatBounds(place.viewport));
                }
            }
        };
        const mapMouseMoveHandler = ({ point }) => {
            // There is an issue with the layer priority
            // This is to avoid to click on this layer when user click on an agency pin
            // This is not ideal...
            const othersFeatures = map.queryRenderedFeatures(point, {
                layers: otherFeaturesLayers,
            });
            const features = map.queryRenderedFeatures(point, {
                layers: [fillLayerId],
            });
            const feature = features[0];
            if (othersFeatures.length > 0) {
                map.setFilter(outlineLayerId, ['in', 'feature_id', '']);
                return;
            }
            if (!feature) {
                // Hide the selection outline layer when the mouse is not over a feature
                map.setFilter(outlineLayerId, ['in', 'feature_id', '']);
                map.getCanvas().style.cursor = 'unset';
                return;
            }
            // Move layer on top
            map.moveLayer(outlineLayerId, SELECTION_LAYERS_ID);
            // Display the selection outline layer when the mouse is over a feature
            map.setFilter(outlineLayerId, ['in', 'feature_id', feature.properties.feature_id]);
            map.getCanvas().style.cursor = 'pointer';
        };
        const mapMouseOutHandler = () => {
            map.setFilter(outlineLayerId, ['in', 'id', '']);
            map.getCanvas().style.cursor = 'unset';
        };
        map.on('click', fillLayerId, (event) => mapClickHandler(event, place.type));
        map.on('mousemove', fillLayerId, mapMouseMoveHandler);
        map.on('mouseout', fillLayerId, mapMouseOutHandler);
    }, [handleBuildingClick, httpClient, language, place.id]);
    /**
     * Remove all the layers of the current place
     */
    const removeAllPlaceLayers = useCallback((map) => {
        removeLayers([
            ...currentPlaceOutlineLayers.current,
            ...currentPlacePriceLayers.current,
            ...selectionLayers.current,
        ], map);
        currentPlaceOutlineLayers.current = [];
        currentPlacePriceLayers.current = [];
        selectionLayers.current = [];
    }, []);
    /**
     * Add a layer to handle agencies pins
     */
    const addAgenciesLayer = useCallback((map) => {
        const sourceName = getLayerSourceName(agenciesLayerId);
        if (!withAgencyPins || typeof map.getLayer(agenciesLayerId) !== 'undefined')
            return;
        {
            map.addSource(sourceName, {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: [] },
            });
            const baseLayer = {
                id: agenciesLayerId,
                type: 'symbol',
                layout: {
                    'icon-image': `agency-pin`,
                    'icon-allow-overlap': true,
                },
                source: sourceName,
            };
            map.addLayer(baseLayer);
            const layerHover = {
                ...baseLayer,
                id: `${agenciesLayerId}-hover`,
                layout: {
                    'icon-image': `agency-pin-hover`,
                },
                filter: ['==', 'id', ''],
            };
            map.addLayer(layerHover);
            const layerActive = {
                ...baseLayer,
                id: `${agenciesLayerId}-active`,
                layout: {
                    'icon-image': `agency-pin-active`,
                },
                filter: ['==', 'id', ''],
            };
            map.addLayer(layerActive);
        }
    }, []);
    /**
     * Bind events on the agencies layer
     */
    const bindAgenciesLayerEvents = useCallback((map) => {
        if (!withAgencyPins)
            return;
        const layers = [agenciesLayerId];
        const allLayers = [
            agenciesLayerId,
            `${agenciesLayerId}-hover`,
            `${agenciesLayerId}-active`,
        ];
        layers.forEach((layerId) => {
            map.on('click', layerId, (event) => {
                const features = event.features;
                if (!features) {
                    return;
                }
                const feature = features[0];
                if (feature.properties.id === currentAgencyId.current) {
                    return;
                }
                map.getCanvas().style.cursor = 'pointer';
                currentAgencyId.current = feature.properties.id;
                map.setFilter(`${layerId}-active`, ['==', 'id', feature.properties.id]);
                displayAgencyCard(map, feature.properties.id, 
                // The geometry type doest not have a coordinates property
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                new LngLat(feature.geometry.coordinates[0], feature.geometry.coordinates[1]));
            });
        });
        map.on('mousemove', (event) => {
            const width = MARKER_SIZE;
            const height = MARKER_SIZE;
            const features = map.queryRenderedFeatures([
                [event.point.x - width / 2, event.point.y - height / 2],
                [event.point.x + width / 2, event.point.y + height / 2],
            ], {
                layers: allLayers,
            });
            if (!features || features.length === 0) {
                map.getCanvas().style.cursor = 'unset';
                layers.forEach((layerId) => {
                    map.setFilter(`${layerId}-hover`, ['==', 'id', '']);
                });
            }
            else if (features[0].properties.id !== currentAgencyId.current) {
                layers.forEach((layerId) => {
                    map.getCanvas().style.cursor = 'pointer';
                    map.setFilter(`${layerId}-hover`, ['==', 'id', features[0].properties.id]);
                });
            }
        });
    }, []);
    const displayAgencyCard = useCallback((map, agencyId, lngLat) => {
        map.fire(MapEvents.SHOW_AGENCY_CARD, { agencyId: agencyId, lngLat });
    }, []);
    /**
     * Update the agencies layer based geographical parameters
     */
    const updateAgenciesLayer = useCallback(async (map, ne, sw) => {
        if (!withAgencyPins)
            return;
        const points = await httpClient.get(`${process.env.BFF_BASE_URL}/agency/map-feature-collection?brand=${contract.nameAbbreviation}&ne=${ne.lat},${ne.lng}&sw=${sw.lat},${sw.lng}&language=${language}`, undefined, process.env.BFF_API_KEY);
        setAgencyPoints(points);
    }, [httpClient, language]);
    /**
     * Update the feature layers based on the current bounds
     */
    const updateAllFeatureLayers = useCallback((map, sw, ne) => {
        updateAgenciesLayer(map, sw, ne);
    }, [updateAgenciesLayer]);
    const updateAllPlaceLayer = useCallback(async (map, placeForPricesLayers) => {
        try {
            /**
             * Add a selection layers to select places (of the same type) around the current place.
             */
            const tileJSON = await getTileLayers();
            const selectionAroundLayersWithZoomRanges = adjustZoomRanges(tileJSON, getZoomRangesByTransactionType(TransactionTypeEnum.SELL, contract.geoConfig, 'selectionLayer'));
            selectionAroundLayersWithZoomRanges.map((layer) => {
                addSelectionLayers(map, layer, 'around', [
                    '!=',
                    ['get', `feature_id`],
                    place.id, // Avoid to select the current place
                ]);
            });
            await addCurrentPlaceOutline(map, place, placeForPricesLayers);
            map.fire(MapEvents.PLACE_LAYERS_ADDED);
        }
        catch (error) {
            map.fire('error');
        }
    }, [addCurrentPlaceOutline, addSelectionLayers, getTileLayers, place]);
    /**
     * Main function to retrieve and display prices and places data
     */
    const updatePlaceLayers = useCallback(async (map) => {
        /**
         * A specific variable to memoize the place used to get prices layers
         * It is not always the current place. e.g. for the place types inside a city
         */
        const placeForPricesLayers = getPlaceForPricesLayers(place);
        let priceTiles;
        if (!placeForPricesLayers) {
            return;
        }
        try {
            if (!activePriceDate.current) {
                activePriceDate.current = await getActivePriceDate(contract.country);
            }
            /**
             * Add layers representing with colors the prices of the place
             */
            priceTiles = await getPriceTilesById(placeForPricesLayers.id);
            const priceLayersWithZoomRanges = adjustZoomRanges(priceTiles, getZoomRangesByTransactionType(TransactionTypeEnum.SELL, contract.geoConfig, 'priceLayer'));
            addAllPriceLayers({
                map,
                placeId: place.id,
                layerCollection: priceLayersWithZoomRanges,
                layersArray: currentPlacePriceLayers.current,
                beforeLayer: PRICE_LAYERS_ID,
            });
            await updateAllPlaceLayer(map, placeForPricesLayers);
            currentLayers.current = priceLayersWithZoomRanges;
            setMapLegendData(getColorScaleByLevel(map, currentLayers.current));
            /**
             * Add layers to select and navigate to places inside the current place
             */
            priceLayersWithZoomRanges.map((layer) => {
                addSelectionLayers(map, layer, 'within');
            });
            map.fire(MapEvents.PRICE_LAYERS_ADDED);
        }
        catch (error) {
            map.fire('error', { error: error });
        }
    }, [getActivePriceDate, getPriceTilesById, place, updateAllPlaceLayer]);
    /**
     * Function called when the map is loaded
     *
     */
    const mapOnLoad = useCallback(async (map) => {
        const canvas = map.getCanvas();
        removeElementFocusability(canvas);
        removeMapControlsFocusability(map.getContainer());
        addAgenciesLayer(map);
        bindAgenciesLayerEvents(map);
        await updatePlaceLayers(map);
        updateLayersBasedOnBounds(map);
        setIsMapLoaded(true);
    }, [addAgenciesLayer, bindAgenciesLayerEvents, updatePlaceLayers]);
    /**
     * Function to remove
     * - agency card popup
     * - agency pins hover
     * - address popup
     *
     * @param map - The current map
     * @param place - The current place
     */
    const removeAllPopup = useCallback((map) => {
        map.fire(MapEvents.REMOVE_POPUP);
        if (withAgencyPins) {
            map.fire(MapEvents.SHOW_AGENCY_CARD);
            currentAgencyId.current = '';
            map.setFilter(`${agenciesLayerId}-active`, ['==', 'id', '']);
        }
    }, []);
    /**
     * Navigate to the new current place (remove all layers, move the map and
     * then display the new layers)
     *
     * @param map - The current map
     * @param place - The current place
     */
    const navigateToPlace = useCallback(async (map, place) => {
        removeAllPopup(map);
        hasForcedZoom.current = false;
        if (!place.viewport) {
            return;
        }
        map.resize();
        map.triggerRepaint();
        // We do not want to remove the layers if the place is a house number and the previous place is a house number in the same municipality
        if (place.typeKey === PlaceTypeKeyEnum.HOUSE_NUMBER &&
            previousPlace?.typeKey === PlaceTypeKeyEnum.HOUSE_NUMBER &&
            place.parents?.municipality?.id === previousPlace.parents?.municipality?.id &&
            place.buildings) {
            updateOutlineLayerSelection(map, place.buildings[0].id);
        }
        else {
            removeAllPlaceLayers(map);
            await updatePlaceLayers(map);
            const { maxZoom } = getZoomBounds({
                place,
                layers: currentLayers.current,
            });
            map.once('moveend', () => {
                ensureZoomWithinLayerBounds({
                    map,
                    place,
                    layers: currentLayers.current,
                    hasForcedZoomRef: hasForcedZoom,
                });
            });
            map.fitBounds(convertToMapBoxLngLatBounds(place.viewport), typeof maxZoom === 'number' ? { maxZoom, duration: MIN_ZOOM_SMOOTHING_MS } : {});
        }
    }, [
        previousPlace?.parents?.municipality?.id,
        previousPlace?.typeKey,
        removeAllPlaceLayers,
        removeAllPopup,
        updateOutlineLayerSelection,
        updatePlaceLayers,
    ]);
    /**
     * Function called to update layers that are based on map bounds
     */
    const updateLayersBasedOnBounds = useCallback((map) => {
        if (map.getZoom() < FEATURES_MIN_ZOOM) {
            return;
        }
        clearTimeout(debounce);
        debounce = window.setTimeout(() => {
            const currentBounds = map.getBounds();
            if (currentBounds) {
                updateAllFeatureLayers(map, currentBounds.getSouthWest(), currentBounds.getNorthEast());
            }
        }, 300);
    }, [updateAllFeatureLayers]);
    useEffect(() => {
        if (!map) {
            return;
        }
        map.on('zoomend', () => {
            setMapLegendData(getColorScaleByLevel(map, currentLayers.current));
            updateLayersBasedOnBounds(map);
        });
        map.on('dragstart', () => {
            removeAllPopup(map);
        });
        map.on('moveend', () => {
            updateLayersBasedOnBounds(map);
        });
        map.once(MapEvents.LOAD, () => {
            mapOnLoad(map);
        });
        map.once(MapEvents.PRICE_LAYERS_ADDED, () => {
            setPriceLayersAdded(true);
        });
        map.once(MapEvents.PLACE_LAYERS_ADDED, () => {
            setPlaceLayersAdded(true);
        });
        map.on(MapEvents.IDLE, () => {
            if (map.loaded() && placeLayersAdded && priceLayersAdded) {
                map.getCanvas()?.setAttribute('data-test', 'map-loaded');
            }
        });
    }, [
        map,
        updateLayersBasedOnBounds,
        mapOnLoad,
        placeLayersAdded,
        priceLayersAdded,
        removeAllPopup,
    ]);
    useEffect(() => {
        if (!map || !place || !isMapLoaded) {
            return;
        }
        navigateToPlace(map, place);
        // eslint-disable-next-line
    }, [place, isMapLoaded]);
    return { map, isMapLoaded, mapLegendData, mapContainerRef, agencyPoints };
}
